import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

import error403 from 'assets/images/Error/403.png';
import error404 from 'assets/images/Error/404.png';
import error500 from 'assets/images/Error/500.png';
import error503 from 'assets/images/Error/503.png';
import Button from 'components/atoms/Button';
import Heading from 'components/atoms/Heading';
import Image from 'components/atoms/Image';
import Text from 'components/atoms/Text';
import Animate from 'components/organisms/Animate';
import Container, { CustomCol, CustomRow } from 'components/organisms/Container';

interface ErrorType {
  imgSrc: string;
  title: string;
  description: string;
}

interface Link {
  url?: string;
  text?: string;
  target?: string;
}

interface ErrorProps {
  type?: number;
  imgSrc?: string;
  title?: string;
  description?: string;
  btnPrimary?: Link;
  btnOutline?: Link;
}

const Error: React.FC<ErrorProps> = ({
  type,
  imgSrc,
  title,
  description,
  btnPrimary,
  btnOutline,
}) => {
  const error: ErrorType | undefined = useMemo(() => {
    switch (type) {
      case 403:
        return {
          imgSrc: error403,
          title: 'Rất tiếc, Quý Khách không thể truy cập trang này.',
          description: 'Vui lòng trở về trang chủ hoặc liên hệ với chúng tôi để được hỗ trợ.',
        };

      case 404:
        return {
          imgSrc: error404,
          title: 'Rất tiếc, chúng tôi không tìm thấy trang này.',
          description: 'Vui lòng trở về trang chủ hoặc liên hệ với chúng tôi để được hỗ trợ.',
        };

      case 500:
        return {
          imgSrc: error500,
          title: 'Lỗi máy chủ nội bộ',
          description: 'Rất tiếc, một vài lỗi không mong muốn đã xảy ra, chúng tôi đang cố gắng khắc phục sự cố. Quý Khách xin vui lòng thử lại sau.',
        };

      case 503:
        return {
          imgSrc: error503,
          title: 'Rất tiếc, hệ thống đang được bảo trì',
          description: 'Trang thông tin Quý Khách tìm kiếm đang trong quá trình bảo trì, vui lòng thử lại sau.',
        };

      default:
        return undefined;
    }
  }, [type]);

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
        <meta name="render:status_code" content="404" />
      </Helmet>
      <div className="t-error u-pt-lg-160 u-pb-lg-160 u-pt-sm-120 u-pb-sm-120 u-pt-80 u-pb-80">
        <Container>
          <CustomRow>
            <CustomCol lg={6}>
              <Animate type="bounceInDown" extendClassName="t-error_thumbnail u-ml-lg-0 u-ml-auto u-mr-lg-0 u-mr-auto">
                <Image ratio="450x253" imgSrc={imgSrc || error?.imgSrc || ''} size="contain" alt="" />
              </Animate>
            </CustomCol>
            <CustomCol lg={6}>
              <Animate type="fadeInRight" extendClassName="t-error_content u-mt-48 u-mt-lg-0">
                <Heading modifiers={['700', 'raisinBlack', 'sm']} type="h4" content={title || error?.title} />
                <div className="u-mt-20">
                  <Text modifiers={['16x24', '400', 'davysGrey']} content={description || error?.description} />
                </div>
                <div className="t-error_group d-flex align-items-center justify-content-center justify-content-lg-start u-mt-40">
                  <div className="t-error_back">
                    <Button
                      useLink
                      isSquare
                      size="xs"
                      href={btnPrimary?.url}
                      target={btnPrimary?.target}
                    >
                      {btnPrimary?.text}
                    </Button>
                  </div>
                  <div className="t-error_contact u-ml-20">
                    <Button
                      useLink
                      isSquare
                      variant="outline"
                      href={btnOutline?.url}
                      target={btnOutline?.target}
                      color="raisinBlack"
                      size="xs"
                    >
                      {btnOutline?.text}
                    </Button>
                  </div>
                </div>
              </Animate>
            </CustomCol>
          </CustomRow>
        </Container>
      </div>
    </>
  );
};

Error.defaultProps = {
  type: undefined,
  imgSrc: undefined,
  title: undefined,
  description: undefined,
  btnPrimary: undefined,
  btnOutline: undefined,
};

export default Error;
